// Time-zone facts for the "time in X" pages, from the Intl time-zone database. Shared by
// the page copy (computed at build) and the live clock component (computed in the browser).

const offsetFormatters = new Map<string, Intl.DateTimeFormat>();

/** UTC offset of `timeZone` at `date`, in minutes (east of UTC is positive). */
export function offsetMinutes(date: Date, timeZone: string): number {
  let fmt = offsetFormatters.get(timeZone);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'longOffset' });
    offsetFormatters.set(timeZone, fmt);
  }
  const part = fmt.formatToParts(date).find((p) => p.type === 'timeZoneName')?.value;
  const m = /GMT([+-])(\d{2}):?(\d{2})?/.exec(part ?? '');
  if (!m) return 0;
  const minutes = Number(m[2]) * 60 + Number(m[3] ?? 0);
  return m[1] === '-' ? -minutes : minutes;
}

export function formatOffset(minutes: number): string {
  if (minutes === 0) return 'UTC+0';
  const sign = minutes > 0 ? '+' : '-';
  const abs = Math.abs(minutes);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  return `UTC${sign}${h}${m ? `:${String(m).padStart(2, '0')}` : ''}`;
}

/** Long name at a moment, e.g. "British Summer Time", "Eastern Standard Time". */
export function zoneName(
  date: Date,
  timeZone: string,
  style: 'long' | 'longGeneric' | 'short' = 'long',
): string {
  return (
    new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: style })
      .formatToParts(date)
      .find((p) => p.type === 'timeZoneName')?.value ?? timeZone
  );
}

export type DstTransition = { at: Date; fromOffset: number; toOffset: number };

/** Offset changes during `year`, found hour by hour (fast enough at build time). */
export function transitionsInYear(timeZone: string, year: number): DstTransition[] {
  const out: DstTransition[] = [];
  let t = Date.UTC(year, 0, 1);
  const end = Date.UTC(year + 1, 0, 1);
  let prev = offsetMinutes(new Date(t), timeZone);
  // Step a day at a time, then narrow to the hour where the offset moved.
  while (t < end) {
    const next = t + 86_400_000;
    const off = offsetMinutes(new Date(next), timeZone);
    if (off !== prev) {
      let lo = t;
      let hi = next;
      while (hi - lo > 60_000) {
        const mid = Math.floor((lo + hi) / 2 / 60_000) * 60_000;
        if (offsetMinutes(new Date(mid), timeZone) === prev) lo = mid;
        else hi = mid;
      }
      out.push({ at: new Date(hi), fromOffset: prev, toOffset: off });
      prev = off;
    }
    t = next;
  }
  return out;
}

/** "Chicago" from "America/Chicago", "Buenos Aires" from "America/Argentina/Buenos_Aires". */
export function zoneCity(timeZone: string): string {
  if (timeZone === 'UTC') return 'UTC';
  return (timeZone.split('/').pop() ?? timeZone).replace(/_/g, ' ');
}

export function describeDifference(minutes: number): string {
  if (minutes === 0) return 'the same time as';
  const abs = Math.abs(minutes);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  const amount = `${h ? `${h} hour${h === 1 ? '' : 's'}` : ''}${h && m ? ' ' : ''}${m ? `${m} minutes` : ''}`;
  return `${amount} ${minutes > 0 ? 'ahead of' : 'behind'}`;
}
